/**
 * Diagnostics ring buffer — §15.5, OPS-04.
 * Bounded by age (14 days) and size (5 MB), whichever comes first. Stores only
 * sanitized events; the sanitizer is applied before anything enters the buffer.
 */

export type DiagnosticsSeverity = 'debug' | 'info' | 'warn' | 'error'

export interface DiagnosticsEvent {
  readonly timestamp: string
  readonly event_code: string
  readonly severity: DiagnosticsSeverity
  readonly context: Readonly<Record<string, string | number | boolean | null>>
}

export interface DiagnosticsBufferOptions {
  readonly max_age_ms?: number
  readonly max_bytes?: number
}

export const DIAGNOSTICS_MAX_AGE_MS = 14 * 24 * 60 * 60 * 1000
export const DIAGNOSTICS_MAX_BYTES = 5 * 1024 * 1024

const encoder = new TextEncoder()

function byteLength(event: DiagnosticsEvent): number {
  return encoder.encode(JSON.stringify(event)).byteLength
}

export class DiagnosticsRingBuffer {
  private events: DiagnosticsEvent[] = []
  private bytes = 0
  private readonly maxAgeMs: number
  private readonly maxBytes: number

  constructor(options: DiagnosticsBufferOptions = {}) {
    this.maxAgeMs = options.max_age_ms ?? DIAGNOSTICS_MAX_AGE_MS
    this.maxBytes = options.max_bytes ?? DIAGNOSTICS_MAX_BYTES
  }

  add(event: DiagnosticsEvent): void {
    this.events.push(event)
    this.bytes += byteLength(event)
    this.enforceSize()
  }

  /** Events still inside the retention window, oldest first. */
  eventsAt(now: number): readonly DiagnosticsEvent[] {
    this.prune(now)
    return [...this.events]
  }

  sizeBytes(): number {
    return this.bytes
  }

  clear(): void {
    this.events = []
    this.bytes = 0
  }

  /** Drop expired events (call on read/write; deterministic via explicit `now`). */
  prune(now: number): void {
    const cutoff = now - this.maxAgeMs
    const kept: DiagnosticsEvent[] = []
    let bytes = 0
    for (const event of this.events) {
      const at = Date.parse(event.timestamp)
      if (Number.isNaN(at) || at >= cutoff) {
        kept.push(event)
        bytes += byteLength(event)
      }
    }
    this.events = kept
    this.bytes = bytes
  }

  private enforceSize(): void {
    while (this.bytes > this.maxBytes && this.events.length > 0) {
      const removed = this.events.shift()
      if (removed !== undefined) this.bytes -= byteLength(removed)
    }
  }
}
